/**
 * EventDispatcherV2 — centralized event binding registry.
 *
 * Mirrors v1 EventDispatcher: namespace-aware getBinding with activeBlocks filtering
 * + kebab↔camelCase conversion for namespace matching.
 */

import type { EventBinding } from "@cogo/types-v2"

type Handler = (payload?: unknown) => void

export class EventDispatcherV2 {
  private handlers = new Map<string, Handler[]>()
  private bindingsByKey: Record<string, EventBinding[]> = {}

  on(key: string, handler: Handler): () => void {
    if (!this.handlers.has(key)) this.handlers.set(key, [])
    this.handlers.get(key)!.push(handler)
    return () => {
      const fns = this.handlers.get(key)
      if (fns) this.handlers.set(key, fns.filter((f) => f !== handler))
    }
  }

  dispatch(elementId: string, eventType: string, payload?: unknown): void {
    const key = `${elementId}:${eventType}`
    this.handlers.get(key)?.forEach((fn) => fn(payload))
  }

  getBinding(elementId: string, eventType: string, activeBlocks?: Set<string>): EventBinding | undefined {
    const key = `${elementId}:${eventType}`
    const arr = this.bindingsByKey[key]
    if (!arr?.length) return undefined
    if (arr.length === 1) return arr[0]
    // Multiple bindings for the same key — pick the one from an active block
    // __ns may be camelCase namespace but activeBlocks uses kebab-case block names
    if (activeBlocks) {
      const match = arr.find((b) => {
        if (!b.__ns) return false
        if (activeBlocks.has(b.__ns)) return true
        // camelCase → kebab-case fallback
        const kebab = b.__ns.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase()
        return activeBlocks.has(kebab)
      })
      if (match) return match
    }
    // Fallback: return the last registered binding
    return arr[arr.length - 1]
  }

  addBinding(elementId: string, eventType: string, binding: EventBinding): void {
    const key = `${elementId}:${eventType}`
    if (!this.bindingsByKey[key]) {
      this.bindingsByKey[key] = [binding]
      return
    }
    // Replace existing binding from same namespace, or append
    const ns = binding.__ns
    const idx = ns ? this.bindingsByKey[key].findIndex((b) => b.__ns === ns) : -1
    if (idx >= 0) {
      this.bindingsByKey[key][idx] = binding
    } else {
      this.bindingsByKey[key].push(binding)
    }
  }
}
